import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { atPacketAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import PageHeader from '../components/PageHeader';
import DeleteIconButton from '../components/DeleteIconButton';
import './AtPackets.css';

const PAGE_SIZE = 25;

/**
 * AT Packets list page. Same pattern as Clients/Providers: search, status filter, table, row actions.
 * Supports ?clientId= or clientIdOverride prop to show only packets for that client (e.g. from client page link).
 * When opened from Edit Client → AT Packets, clientIdOverride is set so the first load is scoped to that client
 * and does not briefly show other clients' packets (avoids race with search params).
 */
function AtPackets({ clientIdOverride = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('clientId') || '';
  const effectiveClientId = (clientIdOverride != null && clientIdOverride !== '') ? String(clientIdOverride) : clientIdFromUrl;
  const { tenantPath } = useTenantFromUrl();
  const [packets, setPackets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [requestSearch, setRequestSearch] = useState('');
  const [statusId, setStatusId] = useState('active');
  const [statuses, setStatuses] = useState([]);
  const [error, setError] = useState(null);
  const [flashNotice, setFlashNotice] = useState(null);
  const alertShownRef = useRef(false);
  const sentinelRef = useRef(null);

  const loadPackets = async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      alertShownRef.current = false;
      const params = { page: pageNum, pageSize };
      const qParts = [clientSearch.trim(), requestSearch.trim()].filter(Boolean);
      if (qParts.length) params.q = qParts.join(' ');
      if (statusId === 'active') {
        params.excludeStatusCodes = 'Approved,PartiallyApproved,Denied';
      } else if (statusId !== '') {
        params.statusId = statusId;
      }
      if (effectiveClientId) params.clientId = effectiveClientId;
      const res = await atPacketAPI.list(params);
      const data = res.data?.data ?? [];
      const newTotal = res.data?.total ?? 0;
      if (append) setPackets((prev) => [...prev, ...data]);
      else setPackets(data);
      setTotal(newTotal);
      setPage(pageNum);
    } catch (err) {
      console.error('Error loading AT packets:', err);
      setPackets([]);
      setTotal(0);
      setError(err.response?.data?.error || err.message);
      if (!alertShownRef.current) {
        alertShownRef.current = true;
        if (err.response?.status === 401) {
          alert('Please log in again.');
          navigate('/login', { replace: true });
        } else {
          alert('Failed to load AT packets');
        }
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useDebouncedSearch(clientSearch, () => loadPackets(1));
  useDebouncedSearch(requestSearch, () => loadPackets(1));

  const hasMore = packets.length < total && total > 0;
  const loadMore = () => { if (!loading && !loadingMore && hasMore) loadPackets(page + 1, true); };
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  useEffect(() => {
    loadPackets(1);
  }, [effectiveClientId, statusId]);
  // requestSearch and clientSearch trigger via debounce; statusId triggers above

  useEffect(() => {
    const summary = location.state?.atPacketNotificationSummary;
    if (!summary) return;
    setFlashNotice(summary);
    navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    atPacketAPI.getStatuses().then((res) => {
      if (!cancelled) setStatuses(res.data?.data ?? []);
    }).catch(() => {
      if (!cancelled) setStatuses([]);
    });
    return () => { cancelled = true; };
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this AT packet? This cannot be undone.')) return;
    try {
      await atPacketAPI.delete(id);
      setPackets((prev) => prev.filter((p) => p.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      console.error('Error deleting AT packet:', err);
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const canDeleteAtPacket = hasPermission('at_packets.delete');

  return (
    <div className="at-packets-list-page">
      <div className="header">
        <PageHeader title={effectiveClientId ? 'AT Packets' : 'Client AT Packets'}>
          <div className="at-packets-header-actions-row">
            {hasPermission('at_packets.read') && (
              <Link
                to={tenantPath(`/lists/at-packets/pending-receipt-items${effectiveClientId ? `?clientId=${encodeURIComponent(effectiveClientId)}` : ''}`)}
                className="btn-at-docs-pending"
              >
                AT Docs – Pending Receipt
              </Link>
            )}
            {hasPermission('at_packets.create') && (
              <Link to={effectiveClientId ? tenantPath(`/client/${effectiveClientId}/at-packets/new`) : tenantPath('/lists/at-packets/new')} className="btn-new">
                + New AT packet
              </Link>
            )}
          </div>
        </PageHeader>
      </div>

      {flashNotice && (
        <div className="at-packets-list-flash" role="status" aria-live="polite">
          <p className="at-packets-success at-packets-success--multiline">{flashNotice}</p>
        </div>
      )}

      <div className="at-packets-pagination-info">
        {loading && packets.length === 0
          ? 'Loading...'
          : total === 0
            ? 'No AT packets found'
            : total === 1
              ? '1 AT packet found'
              : `${total} AT packets found`}
      </div>

      <div className="at-packets-table">
        <table>
          <thead>
            <tr>
              <th>Client Name</th>
              <th>Request Name</th>
              <th>Status</th>
              {canDeleteAtPacket && <th className="col-actions">Delete</th>}
            </tr>
            <tr className="at-packets-filter-row">
              <th>
                <input
                  type="text"
                  className="at-packets-filter-input"
                  placeholder="Search by client..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  aria-label="Filter by client name"
                />
              </th>
              <th>
                <input
                  type="text"
                  className="at-packets-filter-input"
                  placeholder="Search by request name..."
                  value={requestSearch}
                  onChange={(e) => setRequestSearch(e.target.value)}
                  aria-label="Filter by request name"
                />
              </th>
              <th>
                <select
                  className="at-packets-filter-select"
                  value={statusId}
                  onChange={(e) => setStatusId(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="active">Active</option>
                  <option value="">All statuses</option>
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>{s.display_name || s.code}</option>
                  ))}
                </select>
              </th>
              {canDeleteAtPacket && <th className="col-actions" />}
            </tr>
          </thead>
          <tbody>
            {loading && packets.length === 0 ? (
              <tr>
                <td colSpan={canDeleteAtPacket ? 4 : 3} className="no-data loading-cell">Loading AT packets...</td>
              </tr>
            ) : packets.length === 0 ? (
              <tr>
                <td colSpan={canDeleteAtPacket ? 4 : 3} className="no-data">
                  {statusId === 'active'
                    ? (effectiveClientId ? 'No active AT packets for this client.' : 'No active AT packets found.')
                    : 'No AT packets found'}
                </td>
              </tr>
            ) : (
              packets.map((p) => {
                const clientName = [p.client_last_name, p.client_first_name].filter(Boolean).join(', ') || '—';
                return (
                  <tr key={p.id} onClick={() => navigate(tenantPath(`/lists/at-packets/${p.id}`))} className="clickable-row">
                    <td>{clientName}</td>
                    <td>{p.request_name || '—'}</td>
                    <td>{p.status_display_name || p.status_code || '—'}</td>
                    {canDeleteAtPacket && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <DeleteIconButton
                          className="list-btn-delete"
                          onClick={() => handleDelete(p.id)}
                        />
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>
  );
}

export default AtPackets;
